package ui.app.assign.detail.definition

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.focus.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.layout.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.unit.*
import apps.*
import apps.App.Quiz.Question.Type
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import io.*
import kotlinx.coroutines.*
import org.jetbrains.compose.resources.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.utils.*
import tools.*
import ui.app.assign.detail.*
import ui.widget.*


@Composable
fun AppDetailScope.AppDefOfQuiz(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz
) {
    if (display.isMobile) {
        Column {
            DefaultFields(
                nnvl = wrapper,
                modifier = Modifier.fillMaxWidth(),
                mainContent = {
                    Space(16.dp)
                    ContentFields(wrapper, nnvl)
                })
        }
    } else {
        Row {
            Column(Modifier.weight(1F)) {
                DefaultFields(
                    nnvl = wrapper,
                    modifier = Modifier.fillMaxWidth()
                )
            }
            Space(16.dp)
            Column(Modifier.weight(1F)) {
                ContentFields(
                    wrapper = wrapper,
                    nnvl = nnvl
                )
            }
        }
    }
    Space(64.dp)
    LaunchedEffect(Unit) {
        nnvl.setup()
    }
}

@Composable
private fun AppDetailScope.ContentFields(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz
) {
    ColorTheme(
        field = nnvl.backgroundImage,
        label = Res.string.layer_bottom_detail__quiz_label_background,
        canEdit = wrapper.canEdit
    )
    Space(16.dp)
    FieldTextString(
        icon = Svg.Format_text,
        field = nnvl.resultTitle,
        label = Res.string.layer_bottom_detail__quiz_label_result_title,
        canEdit = wrapper.canEdit
    )
    Space(16.dp)
    FieldTextString(
        icon = Svg.Text_long,
        field = nnvl.resultMsg,
        label = Res.string.layer_bottom_detail__quiz_label_result_message,
        canEdit = wrapper.canEdit
    )

}


private val themeVariants = listOf(
    listOf(-21L, -22L),
    listOf(-23L, -24L),
    listOf(-25L, -26L)
)

@Composable
fun AppDetailScope.ColorTheme(
    canEdit: Boolean,
    label: StringResource,
    field: EFieldRegularLong,
    modifier: Modifier = Modifier.width(256.dp)
) {
    Card(modifier = modifier) {
        Column(
            Modifier.padding(horizontal = 16.dp)
                .padding(bottom = 16.dp, top = 8.dp)
        ) {
            Text(label.rememberString())
            Space(8.dp)
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                themeVariants.forEach { items ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        items.forEach { entry ->
                            Box(Modifier.weight(1F)) {
                                val animatedBorder = animateDpAsState(
                                    if (entry == field.state) 8.dp else 0.dp
                                )
                                LazyImage(
                                    id = entry,
                                    contentScale = ContentScale.Crop,
                                    alphaBackground = false,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .aspectRatio(1F)
                                        .clickable(canEdit && entry != field.state) {
                                            field.set(entry)
                                        }
                                        .border(
                                            BorderStroke(
                                                width = animatedBorder.value,
                                                color = Color.White
                                            )
                                        )
                                )
                                this@Row.AnimatedVisibility(
                                    visible = entry == field.state,
                                    modifier = Modifier.align(Alignment.Center),
                                    enter = fadeIn(),
                                    exit = fadeOut()
                                ) {
                                    Icon(
                                        imageVector = Svg.Check,
                                        contentDescription = null,
                                        modifier = Modifier.size(48.dp).alpha(0.9F)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AppDetailScope.AppDefOfQuizQuestions(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz
) {

    var tabSelection by remember {
        mutableStateOf(0)
    }
    val tabLabels = listOf(
        Res.string.layer_bottom_detail__quiz_label_tab_questions,
        Res.string.layer_bottom_detail__quiz_label_tab_answers
    )
    Column(Modifier.fillMaxSize()) {
        TabRow(
            selectedTabIndex = tabSelection,
            modifier = Modifier.fillMaxWidth(),
        ) {
            tabLabels.forEachIndexed { index, stringResource ->
                Tab(selected = tabSelection == index,
                    text = { Text(stringResource.rememberString()) },
                    onClick = { tabSelection = index })
            }
        }
        Crossfade(
            targetState = tabSelection,
            modifier = Modifier.fillMaxSize()
        ) {
            when (it) {
                0 -> QuizPageQuestion(wrapper, nnvl)
                1 -> QuizPageAnswers(wrapper, nnvl)
            }
        }
    }
}


@Composable
fun QuestionCreatorDialog(state: DialogState, nnvl: AppDetailScope.WrapperDefinition.OfQuiz) {
    val data = remember(state.visible) {
        App.Quiz.Question(
            key = GenId.get()
        )
    }
    val binding = remember(state.visible) {
        AppDetailScope.WrapperDefinition.OfQuiz.Question.Item(data, onMakeChange = {})
    }
    QuestionDialogContent(state, binding) {
        if (data.value.isNotEmpty()) {
            data.answers.removeAll { it.value.isEmpty() }
            if (data.answers.isNotEmpty() || Type.TEXT match data.type) {
                nnvl.questions.add(data)
            }
        }
    }
}

@Composable
fun QuestionEditorDialog(
    state: DialogState,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz,
    item: AppDetailScope.WrapperDefinition.OfQuiz.Question.Item
) {
    val data = remember(item) {
        item.data.copy()
    }
    val binding = remember(item) {
        AppDetailScope.WrapperDefinition.OfQuiz.Question.Item(data, onMakeChange = {})
    }
    QuestionDialogContent(
        state = state,
        entry = binding,
        onDelete = {
            state.hide()
            nnvl.questions.removeAll { it.data.key == item.data.key }
        }
    ) {
        if (data.value.isNotEmpty()) {
            data.answers.removeAll { it.value.isEmpty() }
            if (data.answers.isNotEmpty() || data.type == Type.TEXT.type) {
                nnvl.questions.updateItem(item, data)
            }
        }
    }
}

@Composable
fun QuestionDialogContent(
    state: DialogState,
    entry: AppDetailScope.WrapperDefinition.OfQuiz.Question.Item,
    onDelete: (() -> Unit)? = null,
    onConfirm: () -> Unit
) {
    Dialog(state) {
        val selectedType = remember {
            mutableStateOf(
                AppDetailScope.WrapperDefinition.OfQuiz.Question.types.first {
                    it.type.type == entry.type.state
                }
            )
        }
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = {
                Row {
                    Text(
                        text = Res.string.layer_bottom_detail__quiz_label_add_question.rememberString(),
                        modifier = Modifier.weight(1F)
                            .align(Alignment.CenterVertically)
                    )
                    onDelete?.also {
                        SmallFloatingActionButton(
                            onClick = it,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        ) {
                            Icon(Svg.DeleteEmpty, null)
                        }
                    }
                }
            },
            confirmButton = {
                TextButton(
                    enabled = !entry.questionValue.err
                        && (entry.type.state == Type.TEXT.type
                        || entry.answers.state.isNotEmpty()),
                    onClick = {
                        onConfirm()
                        state.hide()
                    }) { Text(Res.string.dialog__action_ok.rememberString()) }
            },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            }) {
            Column(
                modifier = Modifier.width(512.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                TextField(
                    value = entry.questionValue.text,
                    isError = entry.questionValue.err,
                    onValueChange = { entry.questionValue.set(it) },
                    leadingIcon = { Icon(Svg.Tag_text, null) },
                    modifier = Modifier.fillMaxWidth()
                        .clip(CardDefaults.shape),
                    shape = CardDefaults.shape,
                    label = { Text(Res.string.layer_bottom_detail__quiz_label_question_hint.rememberString()) }
                )
                DropDownPickerCard(
                    selected = selectedType,
                    values = AppDetailScope.WrapperDefinition.OfQuiz.Question.types,
                    itemWidth = 512.dp,
                    previewLabel = Res.string.layer_bottom_detail__quiz_answer_type_label.rememberString()
                ) { entry.type.set(it.type.type) }


                val scope = rememberCoroutineScope()

                if (entry.type.state != Type.TEXT.type) {
                    Button(
                        shape = CardDefaults.shape,
                        modifier = Modifier
                            .fillMaxWidth(),
                        onClick = {
                            entry.answers.add(
                                App.Quiz.Answer(
                                    key = GenId.get()
                                )
                            )
                            scope.launch {
                                delay(100)
                                entry.answers.state.lastOrNull()?.focusRequester?.requestFocus()
                            }
                        }) {
                        Icon(Svg.Plus, null)
                        Space(8.dp)
                        Text(text = Res.string.layer_bottom_detail__quiz_action_add_answer.rememberString())
                    }

                    entry.answers.state.forEachIndexed { index, item ->
                        AnswerEntry(index, item) {
                            entry.answers.removeAll { it.key == item.key }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AnswerEntry(
    index: Int,
    item: AppDetailScope.WrapperDefinition.OfQuiz.Answer.Item,
    onDelete: () -> Unit
) {
    TextField(
        value = item.answerValue.text,
        onValueChange = { item.answerValue.set(it) },
        modifier = Modifier.fillMaxWidth()
            .focusRequester(item.focusRequester),
        leadingIcon = { Icon(Svg.Text_long, null) },
        trailingIcon = {
            IconButton(onDelete) {
                Icon(Svg.Delete_outline, null)
            }
        },
        label = { Text("${Res.string.layer_bottom_detail__quiz_label_answer_hint.rememberString()} ${index + 1}") }
    )
}

@Composable
private fun AppDetailScope.QuizPageQuestion(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz
) {
    val creatorState = DialogState.remember()
    QuestionCreatorDialog(creatorState, nnvl)
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Button(
            onClick = { creatorState.show() },
            modifier = Modifier
                .padding(horizontal = 16.dp, vertical = 8.dp)
                .padding(top = 8.dp)
                .fillMaxWidth()
        ) {
            Icon(Svg.Plus, null)
            Space(4.dp)
            Text(Res.string.layer_bottom_detail__quiz_action_add_question.rememberString())
        }
        nnvl.questions.state.forEach {
            QuizPageQuestionEntry(nnvl, it)
        }
    }
}


@Composable
private fun Type.Companion.rememberIcon(type: Int): ImageVector {
    return remember(type) {
        icon(type)
    }
}


@Composable
private fun QuizPageQuestionEntry(nnvl: AppDetailScope.WrapperDefinition.OfQuiz, entry: AppDetailScope.WrapperDefinition.OfQuiz.Question.Item) {
    val editorState = DialogState.remember(key = entry.key)
    QuestionEditorDialog(editorState, nnvl, entry)
    Card(
        onClick = { editorState.show() },
        modifier = Modifier.fillMaxWidth()
            .padding(horizontal = 8.dp)
    ) {
        Column(Modifier.padding(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                Icon(
                    imageVector = Type.rememberIcon(entry.type.state),
                    contentDescription = null,
                    modifier = Modifier.padding(top = 2.dp)
                        .size(20.dp)
                        .align(Alignment.CenterVertically)
                )
                Text(
                    text = entry.questionValue.state,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            entry.answers.state.takeIf { entry.type.state != Type.TEXT.type }
                ?.forEach {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(start = 8.dp)
                    ) {
                        Icon(
                            imageVector = Svg.Forum,
                            contentDescription = null,
                            modifier = Modifier.padding(top = 2.dp)
                                .size(18.dp)
                                .alpha(0.8F)
                                .align(Alignment.CenterVertically)
                        )
                        Text(
                            text = it.answerValue.state,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Light,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                }
        }
    }

}

@Composable
private fun AppDetailScope.QuizPageAnswers(
    wrapper: AppDetailScope.Wrapper,
    nnvl: AppDetailScope.WrapperDefinition.OfQuiz
) {
    val data = remember {
        mutableDataState<IoReport.ReportAppQuiz>(DataState.Loading())
    }

    val isLoading = remember {
        mutableStateOf(false)
    }
    val scope = rememberCoroutineScope()

    fun loadMore() {
        isLoading.value = true
        scope.launch(Dispatchers.Unconfined) {
            runCatching {
                val newData = Repository.api.report.appQuiz(
                    companyId = login.company.id,
                    appId = nnvl.data.appId,
                    offsetReportId = -1L
                )
                val current = data.value.entry ?: IoReport.ReportAppQuiz()
                data.value = DataState.Result(
                    IoReport.ReportAppQuiz(
                        questions = current.questions + newData.questions,
                        answers = current.answers + newData.answers,
                    )
                )
            }.onFailure {
                it.printStackTrace()
            }
            isLoading.value = false
        }
    }
    Box(Modifier.fillMaxSize()) {
        if (data.value is DataState.Loading) {
            CircularProgressIndicator(Modifier.align(Alignment.Center))
        } else if (data.value.entry?.answers.isNullOrEmpty()) {
            Text(
                text = "No votes",
                modifier = Modifier.align(Alignment.Center)
            )
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                contentPadding = PaddingValues(8.dp)
            ) {
                data.value.entry?.also { data ->
                    items(data.answers.sortedByDescending { it.id }) {
                        ReportItemAnswer(data.questions[it.revision]!!, it)
                    }
                }
                item {
                    if ((data.value.entry?.answers?.size ?: 0) % 100 == 0) {
                        Button(
                            onClick = { loadMore() },
                            enabled = isLoading.value
                        ) {
                            Text("Show older")
                        }
                    }
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        data.value = runCatching {
            DataState.Result(
                Repository.api.report.appQuiz(
                    companyId = login.company.id,
                    appId = nnvl.data.appId,
                    offsetReportId = -1L
                )
            )
        }.getOrNull() ?: DataState.Error("fetch fail")
    }
}

@Composable
fun ReportItemAnswer(question: App.Quiz.QuestionStack, answer: IoReport.ReportAppQuiz.AnswerEntry) {
    var isOpen by remember {
        mutableStateOf(false)
    }
    Card(
        onClick = { isOpen = !isOpen },
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(16.dp)
        ) {
            Text(answer.utc.niceFormat())
            AnimatedVisibility(isOpen) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    question.questions.forEach { q ->
                        Card {
                            Column {
                                Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                    Icon(
                                        imageVector = Type.rememberIcon(q.type),
                                        contentDescription = null,
                                        modifier = Modifier.padding(top = 2.dp)
                                            .size(20.dp)
                                            .align(Alignment.CenterVertically)
                                    )
                                    Text(
                                        text = q.value,
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                                answer.data.answers.firstOrNull { it.questionId == q.key }
                                    ?.also { a ->
                                        when (Type[a.type]) {
                                            Type.MULTI -> q.answers
                                                .filter { it.key in a.multiple }
                                                .forEach {
                                                    ReportItemAnswerRow(it.value)
                                                }

                                            Type.SINGLE -> q.answers.firstOrNull { it.key == a.single }?.value?.also {
                                                ReportItemAnswerRow(it)
                                            }

                                            Type.TEXT -> ReportItemAnswerRow(a.text)
                                        }
                                    }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportItemAnswerRow(value: String) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        modifier = Modifier.padding(start = 8.dp)
    ) {
        Icon(
            imageVector = Svg.Forum,
            contentDescription = null,
            modifier = Modifier.padding(top = 2.dp)
                .size(18.dp)
                .alpha(0.8F)
                .align(Alignment.CenterVertically)
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Light,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}
